import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CircleHelp, Copy, Loader2 } from 'lucide-react';
import { DataSurface } from '@/components/platform/DataSurface';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import salesAgentService, { StandaloneContainerType } from '@/lib/api/salesAgent';

interface BrowserAnalyticsSettingsProps {
  orgId: string;
}

const ANALYTICS_EVENTS = [
  { name: 'hobbes_cta_clicked', fires: 'Launcher email field focus or custom trigger click', use: 'Top-of-funnel CTA intent' },
  { name: 'hobbes_lead_submitted', fires: 'Contact form passes frontend validation', use: 'Primary lead conversion' },
  { name: 'hobbes_session_started', fires: 'Visitor enters a ready Hobbes session', use: 'Demo/session starts' },
  { name: 'hobbes_session_engaged', fires: 'Visitor sends their first input after session start', use: 'High-intent engagement' },
  { name: 'hobbes_meeting_clicked', fires: 'Visitor opens a calendar or booking CTA', use: 'Booking intent' },
  { name: 'hobbes_meeting_confirmed', fires: 'Calendar confirmation is detected', use: 'Booked meeting conversion' },
];

const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;

const InfoHint: React.FC<{ copy: string }> = ({ copy }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground"
        aria-label="More information"
      >
        <CircleHelp className="h-4 w-4" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-[260px] text-xs leading-snug">
      {copy}
    </TooltipContent>
  </Tooltip>
);

const CopyButton: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({ title: `${label} copied` });
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast({ title: 'Copy failed', description: 'Please select and copy manually.', variant: 'destructive' });
    }
  }, [label, value]);

  return (
    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 gap-1 text-xs" onClick={handleCopy}>
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
};

export const BrowserAnalyticsSettings: React.FC<BrowserAnalyticsSettingsProps> = ({ orgId }) => {
  const queryClient = useQueryClient();
  const [standaloneContainerType, setStandaloneContainerType] = useState<StandaloneContainerType>('none');
  const [standaloneContainerId, setStandaloneContainerId] = useState('');

  const salesAgentQuery = useQuery({
    queryKey: ['sales-agent', orgId],
    queryFn: async () => {
      const response = await salesAgentService.getSalesAgent(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load analytics settings.');
      }
      return response.data;
    },
  });

  useEffect(() => {
    const salesAgent = salesAgentQuery.data;
    if (!salesAgent) return;
    setStandaloneContainerType(salesAgent.standalone_container_type ?? 'none');
    setStandaloneContainerId(salesAgent.standalone_container_id ?? '');
  }, [salesAgentQuery.data]);

  const updateAnalyticsMutation = useMutation({
    mutationFn: async () => {
      const trimmedContainerId = standaloneContainerId.trim();
      if (standaloneContainerType === 'gtm' && trimmedContainerId && !GTM_CONTAINER_ID_PATTERN.test(trimmedContainerId)) {
        throw new Error('Container ID should look like GTM-XXXXXXX.');
      }

      const response = await salesAgentService.updateSalesAgent(orgId, {
        browser_analytics_enabled: true,
        standalone_container_type: standaloneContainerType,
        standalone_container_id: standaloneContainerType === 'gtm' ? trimmedContainerId || null : null,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to save analytics settings.');
      }

      return response.data;
    },
    onSuccess: (salesAgent) => {
      queryClient.setQueryData(['sales-agent', orgId], salesAgent);
      toast({ title: 'Analytics settings saved' });
    },
    onError: (error) => {
      toast({
        title: 'Could not save analytics settings',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="max-w-5xl">
      <DataSurface>
        <div className="p-4 sm:p-5">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
            <section className="rounded-lg border border-border/80 bg-background p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-base font-semibold tracking-tight text-foreground">GTM setup</h4>
                  <InfoHint copy="Use this to map a Hobbes event into GA4 or Google Ads: create a GTM Custom Event trigger, then fire a GA4 Event tag with the same event name." />
                </div>
                <CopyButton value="hobbes_lead_submitted" label="Custom event trigger" />
              </div>

              <div className="mt-4 divide-y divide-border/70 rounded-md border border-border/80 bg-card">
                <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 px-3 py-2.5 text-sm">
                  <span className="text-muted-foreground">Trigger</span>
                  <code className="truncate text-xs text-foreground">hobbes_lead_submitted</code>
                </div>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 px-3 py-2.5 text-sm">
                  <span className="text-muted-foreground">GA4 event</span>
                  <code className="truncate text-xs text-foreground">hobbes_lead_submitted</code>
                </div>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3 px-3 py-2.5 text-sm">
                  <span className="text-muted-foreground">Convert</span>
                  <span className="text-muted-foreground">Mark in GA4 or Google Ads if desired.</span>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-border/80 bg-background p-4">
              <div className="flex items-center gap-2">
                <h4 className="text-base font-semibold tracking-tight text-foreground">Standalone tracking</h4>
                <InfoHint copy="Standalone pages use the container configured here. Embedded and launcher installs use the tracking already installed on the customer site." />
              </div>

              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="standalone-container-type">Container type</Label>
                  <Select
                    value={standaloneContainerType}
                    onValueChange={(value) => setStandaloneContainerType(value as StandaloneContainerType)}
                    disabled={salesAgentQuery.isLoading}
                  >
                    <SelectTrigger id="standalone-container-type" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="gtm">Google Tag Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="standalone-container-id">Container ID</Label>
                  <Input
                    id="standalone-container-id"
                    value={standaloneContainerId}
                    onChange={(event) => setStandaloneContainerId(event.target.value)}
                    placeholder="GTM-XXXXXXX"
                    disabled={standaloneContainerType !== 'gtm' || salesAgentQuery.isLoading}
                    className="h-9"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => updateAnalyticsMutation.mutate()}
                    disabled={salesAgentQuery.isLoading || updateAnalyticsMutation.isPending}
                    className="h-8 rounded-md gap-2"
                  >
                    {updateAnalyticsMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Save
                  </Button>
                </div>
              </div>
            </section>
          </div>

          <section className="mt-6 border-t border-border/70 pt-6">
            <div className="overflow-x-auto rounded-md border border-border/80">
              <div className="min-w-[680px]">
                <div className="grid grid-cols-[minmax(190px,0.8fr)_minmax(240px,1.2fr)_minmax(170px,0.8fr)] bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                  <span>Event</span>
                  <span>When it fires</span>
                  <span>Recommended use</span>
                </div>
                <div className="divide-y divide-border/70">
                  {ANALYTICS_EVENTS.map((analyticsEvent) => (
                    <div key={analyticsEvent.name} className="grid grid-cols-[minmax(190px,0.8fr)_minmax(240px,1.2fr)_minmax(170px,0.8fr)] gap-3 px-3 py-2.5 text-sm">
                      <code className="text-xs text-foreground">{analyticsEvent.name}</code>
                      <span className="text-muted-foreground">{analyticsEvent.fires}</span>
                      <span className="text-muted-foreground">{analyticsEvent.use}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </DataSurface>
    </div>
  );
};
